import React, { useState } from 'react'
import { Box, Button, Card, CardContent, FormControl, IconButton, useMediaQuery, InputAdornment, InputLabel, OutlinedInput, Stack, SvgIcon, Typography } from '@mui/material'
import { useSnackbar } from 'components/snackbar';
import { useTheme } from '@mui/material/styles';
/**
 * ShareJob
 * 
 * Dialog component to share the job on various platforms, email, linkedin, whatsapp
 * @param {string} jobId - ID of the job to share
 * @param {function} onClose - Handle close of the dialog
 * @param {boolean} invite - Flag to indicate if share or invite
 * @param {string} type - type of the job, navigationTab or jobDetails
 * @returns {React.Component} - Share job dialog component
 */
const ShareJob = ({ jobId, onClose, invite, type }) => {
    const websiteName = `${window?.location?.host}`;
    const id = jobId
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /**
     * Copies the job link to the clipboard and shows a success notification
     * The link is constructed using the current website name and the job id
     * If the copy operation is successful, a success notification is shown
     * with a 2 second delay before it is automatically dismissed
     * If the copy operation fails for any reason, the error is logged in the console
     */
    const handleCopy = () => {
        const jobLink = invite ? websiteName : `${websiteName}/jobs/${id}`;
        navigator.clipboard.writeText(jobLink).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
        enqueueSnackbar('Job link copied to clipboard', { variant: 'success' });
    };

    /**
     * Handles the selection of the platform to share the job on
     * 
     * @param {string} platform - The selected platform to share the job on
     * If no platform is selected, an error notification is shown
     * If the selected platform is 'whatsapp', a new window is opened with the
     * whatsapp web interface and the job link is passed as a parameter
     * If the selected platform is 'linkedin', a new window is opened with the
     * linkedin share interface and the job link is passed as a parameter
     * If the selected platform is 'email', the default email client is opened
     * with the job link in the body of the email
     * After sharing the job, the dialog is closed
     */
    const handlePlatformSelect = (platform) => {
        setSelectedPlatform(platform);
        const jobLink = invite ? websiteName : `${websiteName}/jobs/${id}`;
        if (!platform) {

            const message = type === 'navigationTab'
                ? 'Please select at least one platform to invite your friends.'
                : 'Please select a platform to share the job';

            enqueueSnackbar(message, { variant: 'error' });

            return;
        }
        if (platform === 'whatsapp') {
            window.open(`https://wa.me/?text=${encodeURIComponent(jobLink)}`, '_blank');
        } else if (platform === 'linkedin') {
            window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobLink)}`, '_blank');
        } else if (platform === 'email') {
            window.location.href = `mailto:?subject=Job%20Opportunity&body=${encodeURIComponent(jobLink)}`;
        }
        onClose();
    };

    /**
     * Closes the share job dialog by invoking the onClose callback.
     */

    const handleCloseDialog = () => {
        onClose();
    }


    return (
        <Card variant="outlined" className="share-jobs-card">
            <CardContent>
                <Stack sx={{ marginLeft: "480px", marginTop: '20px' }}>
                    <IconButton
                        className="close-dialog-btn"
                        aria-label="delete"
                        color="primary"
                        onClick={(event) => handleCloseDialog(event)}
                    >
                        <SvgIcon sx={{ width: '20px', height: '20px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <g clipPath="url(#clip0_456_20993)">
                                    <path
                                        d="M7 7L17 17M17 7L7 17L17 7Z"
                                        stroke="#111827"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_456_20993">
                                        <rect width="24" height="24" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </SvgIcon>
                    </IconButton>
                </Stack>
                <Box mb={2.5} sx={{ marginTop: '-35px' }}>
                    <Typography className="title">
                        {invite ? "Invite Friends" : "Share Job"}
                    </Typography>
                </Box>
                <Box mb={2.5} className="share-job-platform">
                    <Stack direction="row" alignItems={'center'} justifyContent={isMobile ? 'space-evenly' : 'flex-start'} spacing={2.5}>
                        <IconButton aria-label="delete" className={selectedPlatform === 'whatsapp' ? 'selected-share-button' : ''} onClick={() => handlePlatformSelect('whatsapp')}>
                            <Box className="icon whatsapp">
                                <SvgIcon>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M16.5 30C24.232 30 30.5 23.732 30.5 16C30.5 8.26801 24.232 2 16.5 2C8.76801 2 2.5 8.26801 2.5 16C2.5 18.5109 3.161 20.8674 4.31847 22.905L2.5 30L9.81486 28.3038C11.8014 29.3854 14.0789 30 16.5 30ZM16.5 27.8462C23.0425 27.8462 28.3462 22.5425 28.3462 16C28.3462 9.45755 23.0425 4.15385 16.5 4.15385C9.95755 4.15385 4.65385 9.45755 4.65385 16C4.65385 18.5261 5.4445 20.8675 6.79184 22.7902L5.73077 26.7692L9.77993 25.7569C11.6894 27.0746 14.0046 27.8462 16.5 27.8462Z" fill="white" />
                                        <path d="M13 9.50038C12.6672 8.8318 12.1565 8.89099 11.6407 8.89099C10.7188 8.89099 9.28125 9.99527 9.28125 12.0505C9.28125 13.7348 10.0234 15.5786 12.5244 18.3366C14.938 20.9984 18.1094 22.3753 20.7422 22.3285C23.375 22.2816 23.9167 20.016 23.9167 19.2508C23.9167 18.9117 23.7062 18.7425 23.5613 18.6965C22.6641 18.266 21.0093 17.4637 20.6328 17.3129C20.2563 17.1622 20.0597 17.3661 19.9375 17.477C19.5961 17.8024 18.9193 18.7613 18.6875 18.977C18.4558 19.1927 18.1103 19.0835 17.9665 19.002C17.4374 18.7897 16.0029 18.1516 14.8595 17.0431C13.4453 15.6723 13.3623 15.2007 13.0959 14.7808C12.8828 14.445 13.0392 14.2389 13.1172 14.1488C13.4219 13.7973 13.8426 13.2545 14.0313 12.9848C14.2199 12.7151 14.0702 12.3055 13.9803 12.0505C13.5938 10.9535 13.2663 10.0353 13 9.50038Z" fill="white" />
                                    </svg>
                                </SvgIcon>
                            </Box>
                            <Typography>WhatsApp</Typography>
                        </IconButton>

                        <IconButton aria-label="delete" className={selectedPlatform === 'linkedin' ? 'selected-share-button' : ''} onClick={() => handlePlatformSelect('linkedin')} >
                            <Box className="icon linkedin">
                                <SvgIcon>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9552 7.4173C10.9552 8.75234 9.79794 9.8346 8.37054 9.8346C6.94306 9.8346 5.78589 8.75234 5.78589 7.4173C5.78589 6.08226 6.94306 5 8.37054 5C9.79794 5 10.9552 6.08226 10.9552 7.4173ZM6.13926 11.6113H10.5574V24.9994H6.13926V11.6113ZM13.2527 11.6121H17.6708V13.3297C18.4124 12.3863 19.8362 11.3435 22.1332 11.3435C26.0009 11.3435 27.1766 13.4224 27.214 17.9342V25.0002H22.7959C22.7959 24.1271 22.7898 23.2493 22.7837 22.3718C22.7752 21.127 22.7666 19.8827 22.7753 18.6533C22.7867 17.0496 22.8013 14.9797 20.3659 14.9797C18.211 14.9797 17.6708 16.5684 17.6708 18.1502V25.0002H13.2527V11.6121Z" fill="white" />
                                    </svg>
                                </SvgIcon>
                            </Box>
                            <Typography>LinkedIn</Typography>
                        </IconButton>

                        <IconButton aria-label="delete" className={selectedPlatform === 'email' ? 'selected-share-button' : ''} onClick={() => handlePlatformSelect('email')} >
                            <Box className="icon email">
                                <SvgIcon>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="18" viewBox="0 0 24 18" fill="none">
                                        <path d="M1.32654 14.8204L0.305468 15.8415C0.113468 15.5011 0 15.0997 0 14.672V3.32657C0 2.89895 0.113454 2.49747 0.305468 2.15712L7.14764 8.99929L1.32654 14.8204ZM23.7119 2.18332L23.2145 2.67204L16.8697 9.01683L23.6945 15.8417C23.8865 15.5013 24 15.0999 24 14.6722V3.3268C24 2.90789 23.8952 2.52369 23.7119 2.18332ZM2.25149 2.25313L11.2494 11.251C11.6683 11.6699 12.3665 11.6786 12.7941 11.251L15.4559 8.58921C15.4559 8.58921 15.4733 8.56303 15.4821 8.5543L22.7867 1.24965C22.4376 1.0402 22.0274 0.926758 21.5998 0.926758H2.39983C1.97221 0.926758 1.57073 1.04021 1.23038 1.23223L2.25149 2.25313ZM21.7481 15.7453L15.9445 9.94178L13.719 12.176C13.2478 12.6385 12.6369 12.8741 12.0172 12.8741C11.3976 12.8741 10.7867 12.6385 10.3241 12.176L8.07248 9.92433L1.23031 16.7665C1.57068 16.9585 1.97211 17.072 2.39976 17.072H21.5997C22.0274 17.072 22.4288 16.9585 22.7692 16.7665L21.7481 15.7453Z" fill="white" />
                                    </svg>
                                </SvgIcon>
                            </Box>
                            <Typography>Email Address</Typography>
                        </IconButton>
                    </Stack>
                </Box>
                <Box mb={4} className="share-job-input-wrapper">
                    <FormControl size="small" fullWidth>
                        <OutlinedInput
                            id="linked-in-profile"
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label=""
                                        edge="end"
                                        onClick={handleCopy}
                                    >
                                        <SvgIcon sx={{ width: '16px', height: '16px' }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="23" height="24" viewBox="0 0 23 24" fill="none">
                                                <path d="M3.73258 7.63819H6.63821V2.90787C6.63821 2.10742 6.96495 1.38008 7.49162 0.853413C8.01829 0.326744 8.74563 0 9.54608 0H20.0921C20.8926 0 21.6199 0.326744 22.1466 0.853413C22.6733 1.38008 23 2.10742 23 2.90787V13.4539C23 14.2544 22.6725 14.9817 22.1466 15.5084C21.6199 16.0351 20.8926 16.3618 20.0921 16.3618H17.1865V21.0921C17.1865 21.8926 16.8598 22.6199 16.3331 23.1466C15.8064 23.6733 15.0791 24 14.2786 24H3.73258C2.93213 24 2.20479 23.6733 1.67812 23.1466C1.15145 22.6199 0.824707 21.8926 0.824707 21.0921V10.5461C0.824707 9.74562 1.15145 9.01828 1.67812 8.49161C2.20479 7.96494 2.93213 7.63819 3.73258 7.63819ZM8.26372 7.63819H14.2786C15.0791 7.63819 15.8064 7.96494 16.3331 8.49161C16.8598 9.01828 17.1865 9.74562 17.1865 10.5461V14.7363H20.0921C20.4443 14.7363 20.765 14.5916 20.997 14.3588C21.2298 14.1261 21.3745 13.8053 21.3745 13.4539V2.90787C21.3745 2.55576 21.2298 2.23499 20.997 2.00224C20.7643 1.76949 20.4435 1.62477 20.0921 1.62477H9.54608C9.19397 1.62477 8.8732 1.76949 8.64045 2.00224C8.4077 2.23499 8.26298 2.55576 8.26298 2.90787V7.63819H8.26372ZM7.45507 9.26371H7.45059H7.44612H3.73183C3.37972 9.26371 3.05895 9.40843 2.82694 9.64118C2.5942 9.87393 2.44947 10.1947 2.44947 10.5468V21.0929C2.44947 21.445 2.5942 21.7658 2.82694 21.9985C3.05969 22.2313 3.38047 22.376 3.73258 22.376H14.2786C14.6307 22.376 14.9515 22.2313 15.1843 21.9985C15.417 21.7658 15.5617 21.445 15.5617 21.0929V15.5494V10.5468C15.5617 10.1947 15.417 9.87393 15.1843 9.64118C14.9515 9.40843 14.6307 9.26371 14.2786 9.26371H7.45507Z" fill="#1F9CD7" />
                                            </svg>
                                        </SvgIcon>
                                    </IconButton>
                                </InputAdornment>
                            }
                            value={invite ? `${websiteName}` : `${websiteName}/jobs/id=${id}`}
                        />
                    </FormControl>
                </Box>
                {/* <Button fullWidth className="continue-btn" variant="contained" size="medium" onClick={onClose}>Done</Button> */}
            </CardContent>
        </Card>
    )
}

export default ShareJob
